/**
 * Duration Utilities
 * Provides duration formatting functions for display and backend communication
 */

/**
 * Format duration for display (uses 'h' instead of ':')
 * @param {string|number} duration - Duration in HH:MM format, HHhMM format, or minutes
 * @returns {string} Formatted duration string (e.g., "01h30")
 */
export const formatDurationForDisplay = (duration) => {
  if (!duration) return '00h00';
  
  // If it's already a string, check if it has ':' or 'h'
  if (typeof duration === 'string') {
    if (duration.includes(':')) {
      // Convert HH:MM to HHhMM
      return duration.replace(':', 'h');
    }
    if (duration.includes('h')) {
      // Already in display format
      return duration;
    }
  }
  
  // If it's a number (minutes), convert to HHhMM
  const minutes = parseInt(duration);
  if (isNaN(minutes)) return '00h00';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}h${mins.toString().padStart(2, '0')}`;
};

/**
 * Format duration for backend (converts 'h' to ':')
 * @param {string} duration - Duration in HHhMM or HH:MM format
 * @returns {string} Duration in HH:MM format for backend
 */
export const formatDurationForBackend = (duration) => {
  if (!duration) return null;
  
  // If it contains 'h', replace with ':'
  if (typeof duration === 'string' && duration.includes('h')) {
    return duration.replace('h', ':');
  }
  
  // If it already contains ':', return as is
  if (typeof duration === 'string' && duration.includes(':')) {
    return duration;
  }
  
  // If it's a number (minutes), convert to HH:MM
  const minutes = parseInt(duration);
  if (isNaN(minutes)) return null;
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Parse duration from backend (HH:MM or HH:MM:SS) and return in display format (HHhMM)
 * @param {string} duration - Duration from backend in HH:MM or HH:MM:SS format
 * @returns {string} Duration in display format (HHhMM)
 */
export const parseDurationFromBackend = (duration) => {
  if (!duration) return null;
  
  // Handle TimeSpan format (HH:MM:SS) by removing seconds
  if (typeof duration === 'string' && duration.includes(':')) {
    const parts = duration.split(':');
    if (parts.length >= 2) {
      // Take only HH and MM parts, convert to display format
      return `${parts[0].padStart(2, '0')}h${parts[1].padStart(2, '0')}`;
    }
  }
  
  return duration;
};

